import React, { FC, useMemo, useState } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import useValueChangeEffect from '../../hooks/useValueChangeEffect';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import { Button, Card, Dropdown, Empty, Menu, message, Pagination, Table } from 'antd';
import {
	CheckOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	DownOutlined,
	FileTextOutlined,
	MoreOutlined,
} from '@ant-design/icons';
import { COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS } from '../../actions/compliance_request_instances_actions';
import {
	complianceRequestInstancesRestCrudThunks,
	approveComplianceRequestInstance,
	fetchComplianceSummary,
} from '../../thunks/compliance_request_instances_thunks';
import {
	fetchComplianceSupplierStatuses,
	updateComplianceSupplierWorkOrderOverride,
} from '../../thunks/compliance_statuses_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { withUserType } from '../withUserType';
import {
	COMPLIANCE_EXPIRY_STATUSES,
	COMPLIANCE_INSTANCE_STATUSES,
	ComplianceRequestInstance,
} from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { deriveComplianceExpiryStatus } from '../../utils/ComplianceUtils';
import {
	BUYER_COMPLIANCE_PIPELINE_FILTER_ITEMS,
	COMPLIANCE_EXPIRY_STATUS_COLORS,
	COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES,
} from '../compliance_common/ComplianceConstants';
import { PipelineFilter } from '../QuotesTable/pipelineFilters/pipelineFilters';
import { ComplianceStatusDisplay } from '../compliance_common/ComplianceStatusDisplay';
import { CompliancePartyBadge } from '../compliance_common/CompliancePartyBadge';
import { RequestedComplianceCell } from '../compliance_common/RequestedComplianceCell';
import { ComplianceEvaluationSummary } from '../compliance_common/ComplianceEvaluationSummary';
import { ComplianceEvaluationModal } from '../compliance_common/ComplianceEvaluationModal';
import RejectComplianceInstanceModal from './RejectComplianceInstanceModal';
import NewComplianceRequestModal from './NewComplianceRequestModal';

const queryString = require('qs');

const OVERVIEW_TARGET_COLLECTION = 'buyerComplianceOverview';
const STATUS_COUNTS_NAME = 'buyerOverviewStatusCounts';
// First draft: country is fixed to US (no UI filter). Sent as a base param on every
// request-instance read/count so the grid + chips are US-scoped.
const US_COUNTRY_PARAMS = { countryCode: 'US' };
// The Overview groups instances by supplier (one supplier row-block, its request
// instances broken out beneath), and there is no supplier-grouped backend read —
// only the flat /request_instances list. So we fetch every matching instance in a
// single page and group + paginate by SUPPLIER client-side. Compliance volume per
// buyer is small; this page size is a safety cap.
//
// BACKEND FOLLOW-UP: replace this 500-row client-side grouping with a server-side
// grouped + paginated compliance read — a party page (supplier/client) plus its
// instances, filter-aware — so the grid fetches only the visible page. That removes
// the cap and the client-side grouping here (and the same in the supplier Overview).
const FETCH_PAGINATION = { current: 1, pageSize: 500 };
const SUPPLIERS_PER_PAGE = 10;

// check / clock / x icons for the derived linked-document expiry status.
const EXPIRY_ICONS = {
	[COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE]: CheckOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON]: ClockCircleOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRED]: CloseCircleOutlined,
};

// The pipeline chips render one count per status, so the count query must ignore
// the currently selected `statuses` — every other filter (supplier / due date /
// search) still applies so the chips follow the grid.
const omitStatuses = (allFilters) => {
	const { statuses, ...rest } = allFilters || {};
	return rest;
};

interface SupplierGroup {
	supplierFacilityId: number;
	supplierFacilityName: string;
	instances: ComplianceRequestInstance[];
}

// A flattened table row: one request instance, tagged with the supplier group it
// belongs to so the Supplier column can render the group header + rowSpan.
interface GroupedInstanceRow extends ComplianceRequestInstance {
	__supplierGroup: SupplierGroup;
}

interface BuyerComplianceOverviewPageProps {
	// Which compliance endpoints to hit. Falls back to the logged-in user's role
	// (session.userType, injected by withUserType) when not explicitly provided.
	roleType?: string;
	history: any;
	location: any;
	instances: ComplianceRequestInstance[];
	loading: boolean;
	// Backend-owned supplier party statuses (redux). The settings-save +
	// request-publish thunks refetch these into redux, so the badges re-render
	// automatically — no refresh signal to observe.
	supplierStatuses: any[];
	statusCounts: { [status: string]: number };
	filters: any;
	refetchInstances: (filters: any, pagination: any) => void;
	fetchStatusCounts: (countFilters: any) => Promise<any>;
	fetchSupplierStatuses: () => Promise<any[]>;
	// Refetches the shell's Non-Compliant summary banner (owned by BuyerCompliancePage
	// via redux) after an approve/reject re-derives party compliance.
	fetchSummary: () => Promise<any>;
	updateSupplierOverride: (body: {
		supplierFacilityId: number;
		allowWorkOrdersWhenNonCompliant: boolean | null;
	}) => Promise<any[]>;
	updateFilters: (filters: any, targetCollectionName: string) => void;
	approveInstance: (id: number) => Promise<any>;
}

// Matches CompliancePartyBadge's non-compliant red so the override control reads
// as part of the badge block.
const NON_COMPLIANT_RED = 'rgb(204, 50, 50)';

// Backend party-status list -> map keyed by supplierFacilityId so each supplier
// header can look up its own Compliant / Non-Compliant badge state.
const buildSupplierStatusMap = (statuses: any[]) =>
	(statuses || []).reduce((accumulated, status) => {
		accumulated[status.supplierFacilityId] = status;
		return accumulated;
	}, {});

// Group the flat instance list into one entry per supplier, preserving a stable
// (name-sorted) supplier order. "Only matching suppliers": the source list is
// already filtered server-side, so a supplier only appears if it has a matching
// instance.
const groupInstancesBySupplier = (instances: ComplianceRequestInstance[]): SupplierGroup[] => {
	const groupsById: { [id: number]: SupplierGroup } = {};
	const order: number[] = [];
	(instances || []).forEach((instance) => {
		const supplierFacilityId = instance.supplierFacilityId;
		if (!groupsById[supplierFacilityId]) {
			groupsById[supplierFacilityId] = {
				supplierFacilityId,
				supplierFacilityName:
					nullSafeGet('supplierFacilityName', instance) ||
					nullSafeGetOrElse('supplierFacility.name', instance, '--'),
				instances: [],
			};
			order.push(supplierFacilityId);
		}
		groupsById[supplierFacilityId].instances.push(instance);
	});
	return order
		.map((id) => groupsById[id])
		.sort((left, right) =>
			(left.supplierFacilityName || '').localeCompare(right.supplierFacilityName || '')
		);
};

const BuyerComplianceOverviewPage: FC<BuyerComplianceOverviewPageProps> = ({
	roleType,
	userType,
	history,
	location,
	instances,
	loading,
	supplierStatuses,
	statusCounts,
	filters,
	refetchInstances,
	fetchStatusCounts,
	fetchSupplierStatuses,
	fetchSummary,
	updateSupplierOverride,
	updateFilters,
	approveInstance,
}): React.ReactElement => {
	const role = roleType || userType;
	const [rejectingInstance, setRejectingInstance] = useState(null);
	const [viewingEvaluationInstance, setViewingEvaluationInstance] = useState(null);
	const [editingRequest, setEditingRequest] = useState(null);
	const [supplierPage, setSupplierPage] = useState(1);

	// Party badges come straight from redux; keyed by supplierFacilityId for
	// per-supplier lookup in the Supplier cell.
	const supplierStatusMap = useMemo(
		() => buildSupplierStatusMap(supplierStatuses),
		[supplierStatuses]
	);

	const statuses = nullSafeGet('statuses', filters);
	// Serialized filter keys — the row fetch follows every filter, the chip counts
	// follow every filter except `statuses`.
	const allFiltersKey = JSON.stringify(filters);
	const countFiltersKey = JSON.stringify(omitStatuses(filters));

	// Refetch the supplier party statuses into redux (drives the badges). Called on
	// mount and after any instance change (approve/reject) so badges stay in sync.
	const loadSupplierStatuses = () => fetchSupplierStatuses();

	// Keep the filters mirrored to the URL query string, matching what
	// PaginatedReduxTable's `updateQueryParams` did: AdvancedFilters derives its
	// initial search from the URL, so without this its clear-search path can fail
	// to refetch. Kept identical to avoid regressing that shared behavior.
	const syncFiltersToUrl = (nextFilters) => {
		const params = queryString.stringify({ ...nextFilters, ...US_COUNTRY_PARAMS });
		history.replace(`${location.pathname}?${params}`);
	};

	useMountEffect(() => {
		refetchInstances(filters, FETCH_PAGINATION);
		fetchStatusCounts(omitStatuses(filters));
		loadSupplierStatuses();
	});

	// Rows follow every filter change; jump back to the first supplier page. Chip
	// counts are refetched by the dedicated countFiltersKey effect below (which also
	// covers status-only changes), so they're intentionally NOT refetched here to
	// avoid a duplicate racing count request.
	useValueChangeEffect(allFiltersKey, () => {
		refetchInstances(filters, FETCH_PAGINATION);
		setSupplierPage(1);
		syncFiltersToUrl(filters);
	});
	useValueChangeEffect(countFiltersKey, () => {
		fetchStatusCounts(omitStatuses(filters));
	});

	const supplierGroups = useMemo(() => groupInstancesBySupplier(instances), [instances]);

	// Clamp to a valid page: an approve/reject can shrink the supplier list below
	// the current page, which would otherwise render an empty table over real data.
	const totalSupplierPages = Math.max(1, Math.ceil(supplierGroups.length / SUPPLIERS_PER_PAGE));
	const effectiveSupplierPage = Math.min(supplierPage, totalSupplierPages);

	// Slice the supplier groups to the current page, then flatten to instance rows.
	// The first row of each group carries rowSpan = groupSize so the Supplier cell
	// merges across that supplier's request rows (antd merged-cell idiom).
	const { rows, rowSpanByInstanceId } = useMemo(() => {
		const start = (effectiveSupplierPage - 1) * SUPPLIERS_PER_PAGE;
		const pagedGroups = supplierGroups.slice(start, start + SUPPLIERS_PER_PAGE);
		const flatRows: GroupedInstanceRow[] = [];
		const spanMap: { [instanceId: number]: number } = {};
		pagedGroups.forEach((group) => {
			group.instances.forEach((instance, indexInGroup) => {
				flatRows.push({ ...instance, __supplierGroup: group });
				spanMap[instance.id] = indexInGroup === 0 ? group.instances.length : 0;
			});
		});
		return { rows: flatRows, rowSpanByInstanceId: spanMap };
	}, [supplierGroups, effectiveSupplierPage]);

	const pipelineValue = { statuses };
	const handlePipelineChange = (nextValue) =>
		updateFilters({ statuses: nullSafeGet('statuses', nextValue) }, OVERVIEW_TARGET_COLLECTION);

	const refreshAfterReview = () => {
		refetchInstances(filters, FETCH_PAGINATION);
		fetchStatusCounts(omitStatuses(filters));
		loadSupplierStatuses();
		// Approve/reject can flip a supplier's compliance, so refresh the shell banner.
		fetchSummary();
	};

	const handleApprove = (instance) => {
		approveInstance(instance.id).then(refreshAfterReview);
	};
	const openRejectModal = (instance) => setRejectingInstance(instance);
	const closeRejectModal = () => setRejectingInstance(null);
	const closeEvaluationModal = () => setViewingEvaluationInstance(null);
	const handleRejected = () => {
		setRejectingInstance(null);
		refreshAfterReview();
	};

	// Edit the parent compliance request (fans out to all its instances). The
	// backend allows editing Draft + Published requests, and the Overview only
	// shows Published-request instances, so Edit is available on every row.
	const openEditRequest = (instance) =>
		setEditingRequest(nullSafeGet('complianceRequest', instance));
	const closeEditRequest = () => setEditingRequest(null);
	const handleEditSuccess = () => {
		setEditingRequest(null);
		refreshAfterReview();
	};

	// Per-supplier work-order override. Sends the new value to
	// PUT /compliance/supplier_statuses (true = allow, false = block, null = clear
	// the override and inherit the org-wide default) and rebuilds the status map
	// from the refreshed list the endpoint returns.
	const handleOverrideChange = (supplierFacilityId, value: boolean | null) => {
		const hideLoader = message.loading('Updating work order setting...', 0);
		// The thunk stores the refreshed statuses into redux, so the badge/label
		// update on their own — no local state to set here.
		updateSupplierOverride({
			supplierFacilityId,
			allowWorkOrdersWhenNonCompliant: value,
		})
			.then(() => {
				hideLoader();
				message.success('Work order setting updated.');
			})
			.catch(() => {
				hideLoader();
				message.error('Could not update the work order setting.');
			});
	};

	// Interactive "can / cannot send work orders" control for a non-compliant
	// supplier. The label reflects the effective canReceiveWorkOrders; the menu
	// lets the buyer force-allow, force-block, or fall back to the org default.
	const renderWorkOrderOverride = (status) => {
		const canSend = status.canReceiveWorkOrders;
		const supplierFacilityId = status.supplierFacilityId;
		const overrideMenu = (
			<Menu selectable={false}>
				<Menu.Item key="allow" onClick={() => handleOverrideChange(supplierFacilityId, true)}>
					Allow work orders
				</Menu.Item>
				<Menu.Item key="block" onClick={() => handleOverrideChange(supplierFacilityId, false)}>
					Don't allow work orders
				</Menu.Item>
				<Menu.Item key="default" onClick={() => handleOverrideChange(supplierFacilityId, null)}>
					Use default setting
				</Menu.Item>
			</Menu>
		);
		return (
			<Dropdown overlay={overrideMenu} trigger={['click']}>
				<span
					className="footnoteregular inline-flex w-fit cursor-pointer flex-row items-center gap-1"
					style={{ color: canSend ? undefined : NON_COMPLIANT_RED }}
				>
					{canSend ? 'Can send work orders' : 'Cannot send work orders'}
					<DownOutlined style={{ fontSize: 10 }} />
				</span>
			</Dropdown>
		);
	};

	// Supplier column: one merged cell per supplier group (Compliant / Non-Compliant
	// badge + name), spanning that supplier's request rows. Non-compliant suppliers
	// get the interactive work-order override in place of the badge's static line.
	const renderSupplierCell = (row: GroupedInstanceRow) => {
		const group = row.__supplierGroup;
		const status = supplierStatusMap[group.supplierFacilityId];
		return {
			children: (
				<div className="flex flex-col gap-1">
					<CompliancePartyBadge
						status={status}
						perspective="buyer"
						workOrderControl={status ? renderWorkOrderOverride(status) : undefined}
					/>
					<span className="bodymedium">{group.supplierFacilityName}</span>
				</div>
			),
			props: { rowSpan: rowSpanByInstanceId[row.id] },
		};
	};

	const renderRequestedCompliance = (instance) => (
		<RequestedComplianceCell
			complianceRequest={nullSafeGetOrElse('complianceRequest', instance, {})}
		/>
	);

	const renderRequestDetails = (instance) => {
		const request = nullSafeGetOrElse('complianceRequest', instance, {});
		const locationLabel = request.allLocations
			? 'All'
			: `${nullSafeGetOrElse('locationIds', request, []).length} locations`;
		return (
			<div className="footnoteregular flex flex-col gap-1 text-character-secondary">
				<span className="bodyregular text-character-primary">
					{request.isMandatory ? 'Mandatory' : 'Optional'}
					{request.requiresUniqueDocument ? ' • Unique Doc' : ''}
				</span>
				<span>{locationLabel}</span>
			</div>
		);
	};

	const renderStatus = (instance) => {
		const dueBy = nullSafeGet('complianceRequest.dueBy', instance);
		// The AI check stored when the supplier linked this document. Absent for
		// documents linked before the check shipped, or when it was skipped.
		const checklistEvaluation = nullSafeGet('checklistEvaluation', instance);
		const openEvaluation = () => setViewingEvaluationInstance(instance);
		return (
			<div className="flex flex-col gap-1">
				<ComplianceStatusDisplay status={instance.status} perspective="buyer" />
				{checklistEvaluation ? (
					<ComplianceEvaluationSummary
						evaluation={checklistEvaluation}
						checklistItems={nullSafeGetOrElse(
							'complianceRequest.checklistExtraction.items',
							instance,
							[]
						)}
						onViewDetails={openEvaluation}
					/>
				) : null}
				{dueBy ? (
					<span className="footnoteregular text-character-secondary">
						Due: {moment(dueBy).format('D MMM, YYYY')}
					</span>
				) : null}
				{instance.rejectionReason ? (
					<span className="footnoteregular text-character-secondary">
						{instance.rejectionReason}
					</span>
				) : null}
			</div>
		);
	};

	// Compliance Expiry: the validity window of the document linked to this
	// instance. No server expiryStatus exists, so it is derived from the linked
	// document's expiryDate (Expired if past / Expiring soon within 30 days). Blank
	// until a document is linked. The attention states (soon / expired) get a
	// colored label + icon; an up-to-date document shows just its expiry date.
	const renderExpiry = (instance) => {
		const expiryDate = nullSafeGet('linkedComplianceDocument.expiryDate', instance);
		if (!expiryDate) {
			return <span className="text-character-secondary">--</span>;
		}
		const status = deriveComplianceExpiryStatus(expiryDate);
		const needsAttention =
			status === COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON ||
			status === COMPLIANCE_EXPIRY_STATUSES.EXPIRED;
		const color = needsAttention ? COMPLIANCE_EXPIRY_STATUS_COLORS[status] : undefined;
		const IconComponent = needsAttention ? EXPIRY_ICONS[status] : null;
		return (
			<div className="flex flex-col gap-1">
				{needsAttention ? (
					<span className="bodymedium inline-flex flex-row items-center gap-1" style={{ color }}>
						{IconComponent ? <IconComponent /> : null}
						{COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES[status]}
					</span>
				) : null}
				<span
					className={needsAttention ? 'footnoteregular' : 'bodyregular'}
					style={color ? { color } : undefined}
				>
					{moment(expiryDate).format('D MMM, YYYY')}
				</span>
			</div>
		);
	};

	const renderDoc = (instance) => {
		const fileUrl = nullSafeGet('linkedComplianceDocument.fileUrl', instance);
		if (!fileUrl) {
			return <span className="text-character-secondary">--</span>;
		}
		return (
			<Button icon={<FileTextOutlined />} href={fileUrl} target="_blank" rel="noopener noreferrer">
				VIEW DOCUMENT
			</Button>
		);
	};

	const renderActions = (instance) => {
		const canReview = instance.status === COMPLIANCE_INSTANCE_STATUSES.PENDING_REVIEW;
		const sampleFileUrl = nullSafeGet('complianceRequest.sampleFileUrl', instance);
		const handleApproveClick = () => handleApprove(instance);
		const handleRejectClick = () => openRejectModal(instance);
		const handleEditClick = () => openEditRequest(instance);
		const openSample = () => window.open(sampleFileUrl, '_blank', 'noreferrer');
		const menu = (
			<Menu>
				<Menu.Item key="approve" disabled={!canReview} onClick={handleApproveClick}>
					Approve
				</Menu.Item>
				<Menu.Item key="reject" disabled={!canReview} danger={true} onClick={handleRejectClick}>
					Reject
				</Menu.Item>
				{sampleFileUrl ? (
					<Menu.Item key="sample" onClick={openSample}>
						View Sample
					</Menu.Item>
				) : null}
				<Menu.Item key="edit" onClick={handleEditClick}>
					Edit Request
				</Menu.Item>
			</Menu>
		);
		return (
			<Dropdown overlay={menu} trigger={['click']}>
				<Button icon={<MoreOutlined />} />
			</Dropdown>
		);
	};

	const tableColumns = [
		{
			title: 'Supplier',
			key: 'supplier',
			width: '16%',
			render: (text, row: GroupedInstanceRow) => renderSupplierCell(row),
		},
		{
			title: 'Requested Compliance',
			key: 'requestedCompliance',
			width: '20%',
			render: (text, instance) => renderRequestedCompliance(instance),
		},
		{
			title: 'Request Details',
			key: 'details',
			width: '13%',
			render: (text, instance) => renderRequestDetails(instance),
		},
		{
			title: 'Status',
			key: 'status',
			width: '16%',
			render: (text, instance) => renderStatus(instance),
		},
		{
			title: 'Compliance Expiry',
			key: 'expiry',
			width: '13%',
			render: (text, instance) => renderExpiry(instance),
		},
		{
			title: 'Compliance Doc',
			key: 'doc',
			width: '12%',
			render: (text, instance) => renderDoc(instance),
		},
		{
			title: '',
			key: 'actions',
			width: 48,
			align: 'right' as const,
			render: (text, instance) => renderActions(instance),
		},
	];

	const pipelineCounts = BUYER_COMPLIANCE_PIPELINE_FILTER_ITEMS.reduce(
		(accumulated, item) => ({
			...accumulated,
			[item.name]: nullSafeGetOrElse(item.name, statusCounts, 0),
		}),
		{}
	);

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description="Supplier compliance statuses will appear here once compliance requests are sent."
		/>
	);

	return (
		<div className="flex flex-col gap-3 pb-6">
			<PipelineFilter
				items={BUYER_COMPLIANCE_PIPELINE_FILTER_ITEMS}
				subItems={{}}
				value={pipelineValue}
				onChange={handlePipelineChange}
				counts={pipelineCounts}
			/>
			<Card bordered={false} bodyStyle={{ padding: 8 }}>
				<Table
					dataSource={rows}
					columns={tableColumns}
					rowKey={(row) => row.id}
					loading={loading}
					pagination={false}
					locale={{ emptyText: emptyState }}
				/>
				{supplierGroups.length > SUPPLIERS_PER_PAGE ? (
					<div className="mt-3 flex flex-row justify-end">
						<Pagination
							current={effectiveSupplierPage}
							pageSize={SUPPLIERS_PER_PAGE}
							total={supplierGroups.length}
							showSizeChanger={false}
							onChange={setSupplierPage}
						/>
					</div>
				) : null}
			</Card>
			{rejectingInstance && (
				<RejectComplianceInstanceModal
					visible={!!rejectingInstance}
					roleType={role}
					instance={rejectingInstance}
					onCancel={closeRejectModal}
					onSuccess={handleRejected}
				/>
			)}
			{viewingEvaluationInstance && (
				<ComplianceEvaluationModal
					visible={!!viewingEvaluationInstance}
					instance={viewingEvaluationInstance}
					onCancel={closeEvaluationModal}
				/>
			)}
			{editingRequest && (
				<NewComplianceRequestModal
					visible={!!editingRequest}
					roleType={role}
					editingRequest={editingRequest}
					onCancel={closeEditRequest}
					onSuccess={handleEditSuccess}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => {
	const slice = state.compliance_request_instances;
	return {
		instances: getRecordsForTargetCollection(slice, OVERVIEW_TARGET_COLLECTION),
		loading: nullSafeGetOrElse(`${OVERVIEW_TARGET_COLLECTION}.fetching`, slice, false),
		supplierStatuses: nullSafeGetOrElse('supplierStatuses', state.compliance_party_statuses, []),
		statusCounts: nullSafeGetOrElse(`counts.${STATUS_COUNTS_NAME}`, slice, {}),
		filters: nullSafeGetOrElse(`${OVERVIEW_TARGET_COLLECTION}.filters`, slice, {}),
	};
};

const mapDispatchToProps = (dispatch, ownProps) => {
	const role = ownProps.roleType || ownProps.userType;
	return {
		// Fetch every matching instance for the current filters into the overview
		// collection. Grouping + supplier pagination happen client-side, so this is
		// a single generous page (see FETCH_PAGINATION).
		refetchInstances: (filters, pagination) =>
			dispatch(
				complianceRequestInstancesRestCrudThunks(role).read(
					{ ...US_COUNTRY_PARAMS },
					OVERVIEW_TARGET_COLLECTION,
					pagination,
					null,
					filters
				)
			),
		fetchStatusCounts: (countFilters) =>
			dispatch(
				complianceRequestInstancesRestCrudThunks(role).countBy(
					{ countBy: 'status', ...US_COUNTRY_PARAMS, ...countFilters },
					STATUS_COUNTS_NAME
				)
			),
		updateFilters: (filters, targetCollectionName) =>
			dispatch(
				COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateFilters(
					filters,
					targetCollectionName
				)
			),
		approveInstance: (id) => dispatch(approveComplianceRequestInstance(role)(id)),
		fetchSupplierStatuses: () => dispatch(fetchComplianceSupplierStatuses(role)()),
		fetchSummary: () => dispatch(fetchComplianceSummary(role)()),
		updateSupplierOverride: (body) =>
			dispatch(updateComplianceSupplierWorkOrderOverride(role)(body)),
	};
};

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(BuyerComplianceOverviewPage))
);
